import React, { useRef, useState, FC, useEffect } from 'react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';

import * as styles from './Accordion.css';

interface AccordionCustomization {
  padding: string;
  contentBg: string;
  accordionBg: string;
}

interface AccordionProps {
  title: string;
  hasError: boolean;
  children: React.ReactNode;
  customizations: AccordionCustomization;
}

const default_customizations: AccordionCustomization = {
  padding: `0.6rem 8px`,
  contentBg: '#F1F3F9',
  accordionBg: '#F1F3F9',
};

export const Accordion = ({
  title,
  hasError,
  children,
  customizations,
  maxWidth = '800px',
  isPlaceholder = false,
}: {
  title;
  hasError;
  children;
  customizations;
  maxWidth?;
  isPlaceholder?: boolean;
}) => {
  const mergedCustomization = {
    ...default_customizations,
    ...customizations,
  };

  const contentRef = useRef(null);

  const [isOpenAccordion, setIsOpenAccordion] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const toggleAccordion = () => setIsOpenAccordion((prevState) => !prevState);
  const onContentHandler = (e) => e.stopPropagation();

  useEffect(() => {
    setContentHeight(contentRef.current.offsetHeight);
  }, []);

  return (
    <div
      onClick={toggleAccordion}
      className={styles.wrapper({
        hasBorder: !isOpenAccordion && !!hasError,
      })}
      style={{
        maxWidth,
        ...assignInlineVars({
          [styles.accordionBgVar]: mergedCustomization.accordionBg,
          [styles.contentBgVar]: mergedCustomization.contentBg,
          [styles.contentPaddingVar]:
            mergedCustomization.padding ?? '0.6rem 8px',
        }),
      }}
    >
      <div className={styles.accordion}>
        <div className={styles.titleWrapper}>
          <div className={styles.title({ isPlaceholder })}>{title}</div>
        </div>
        <div className={styles.accordionIcon}>
          {isOpenAccordion ? (
            <FontAwesomeIcon icon={faMinus as any} />
          ) : (
            <FontAwesomeIcon icon={faPlus as any} />
          )}
        </div>
      </div>

      <div className={styles.contentWrapper({ isOpen: isOpenAccordion })}>
        <div
          className={styles.content}
          ref={contentRef}
          onClick={onContentHandler}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
